import React, { useCallback, useEffect, useState } from 'react'
import { AppMessageColumnViewProps, AppMessageDataRecord } from './types'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { AbilityAction } from '../../enums/ability-action.enum'
import { asCaslSubject, getDemoAttributesFor } from '../../stores/app-ability'
import { IStatusTypeValue, StatusType } from '../../enums/status-type'
import { Button } from '@pushly/aqe/lib/components'
import { useDataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { AppMessageListType } from './enums'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../stores/app'
import { AppService } from '../../services/index'
import { buildPrimaryLinkUrl, buildDuplicateUrl, buildEditUrl } from './helpers'
import { AppMessageScheduleService } from '../../services/app-message-schedule.service'
import { MenuProps } from 'antd'
import { PlatformLevel } from '@pushly/aqe/lib/types/platform-level'

type AppMessageActionColumnViewProps = AppMessageColumnViewProps & {
    onArchiveClick(item: any): void
}

export const AppMessageActionColumnView = (props: AppMessageActionColumnViewProps) => {
    const [state, _dispatch, requestDataSourceReload] = useDataViewContext<AppMessageDataRecord>()
    const { type, data } = props
    const level = state.levelResource.level as PlatformLevel

    const appState = useService(AppState)
    const appSvc = useService(AppService)
    const appMessageScheduleSvc = useService(AppMessageScheduleService)

    const isAppMessageDataView = type === AppMessageListType.APP_MESSAGE
    const isTemplateDataView = type === AppMessageListType.TEMPLATE

    let abilityEntity = SubjectEntity.APP_MESSAGE
    let abilityConstraint: any = {}
    abilityConstraint.domainId = state.levelResource.id
    Object.assign(abilityConstraint, getDemoAttributesFor(state.levelResource.id, appState.currentDomain))

    const canMutate = appState.abilityStore.can(AbilityAction.UPDATE, asCaslSubject(abilityEntity, abilityConstraint))

    const [primaryLinkUrl, setPrimaryLinkUrl] = useState(buildPrimaryLinkUrl(level, data, appSvc.routeWithin))
    useEffect(() => {
        setPrimaryLinkUrl(buildPrimaryLinkUrl(level, data, appSvc.routeWithin))
    }, [type, level, state.levelResource.id, data.id])

    const [editUrl, setEditUrl] = useState(buildEditUrl(level, data, appSvc.routeWithin))
    useEffect(() => {
        setEditUrl(buildEditUrl(level, data, appSvc.routeWithin))
    }, [type, level, state.levelResource.id, data.id])

    const [duplicateUrl, setDuplicateUrl] = useState(buildDuplicateUrl(level, data, appSvc.routeWithin))
    useEffect(() => {
        setDuplicateUrl(buildDuplicateUrl(level, data, appSvc.routeWithin))
    }, [type, state.levelResource.level, state.levelResource.id, data.id])

    const handleStatusChange = useCallback(
        async ({ name }: IStatusTypeValue) => {
            await appMessageScheduleSvc.updateAppMessageSchedule(state.levelResource.id, data.id!, { status: name })
            requestDataSourceReload()
        },
        [type, state.levelResource.level, state.levelResource.id, data.id],
    )

    const isCancellable = true

    let actions: any = []

    if (!props.nativeDisabled && canMutate) {
        actions.push(
            {
                text: 'Edit',
                icon: 'edit',
                onClick: () => appSvc.route(editUrl),
                data,
                altHref: editUrl,
            },
            {
                text: 'Duplicate',
                icon: 'copy',
                onClick: () => appSvc.route(duplicateUrl),
                data,
                altHref: duplicateUrl,
            },
        )

        if (isAppMessageDataView && 'status' in data) {
            if (actions.length) {
                actions.push({ divider: true })
            }

            if (data.status === StatusType.ACTIVE.name) {
                actions.push({
                    text: 'Pause',
                    icon: 'pause-circle',
                    onClick: () => handleStatusChange(StatusType.PAUSED),
                    data,
                })
            } else if (data.status === StatusType.PAUSED.name) {
                actions.push({
                    text: 'Activate',
                    icon: 'play-circle',
                    onClick: () => handleStatusChange(StatusType.ACTIVE),
                    data,
                })
            }
        }
    }

    if (
        canMutate &&
        isCancellable &&
        isAppMessageDataView &&
        'status' in data &&
        [StatusType.ACTIVE.name, StatusType.PAUSED.name].includes(data.status)
    ) {
        if (actions.length) {
            actions.push({ divider: true })
        }

        actions.push({
            text: 'Cancel',
            icon: 'close',
            onClick: () => handleStatusChange(StatusType.CANCELLED),
            data,
        })
    }

    const canUserArchive =
        appState.currentUser?.isInternalUser === true ||
        appState.abilityStore.can(AbilityAction.UPDATE, appState.abilityStore.currentDomainIdentity)

    if (
        state.levelResource.level === 'domain' &&
        canUserArchive &&
        (('status' in data && [StatusType.COMPLETED.name, StatusType.CANCELLED.name].includes(data.status)) ||
            isTemplateDataView)
    ) {
        actions.push({
            text: 'Archive',
            icon: 'folder',
            onClick: props.onArchiveClick,
            disabled: !isAppMessageDataView && props.nativeDisabled,
            data,
        })
    }

    if (actions.length === 0) {
        actions = undefined
    }

    return (
        <Button
            actions={actions}
            onClick={() => appSvc.route(primaryLinkUrl)}
            size="small"
            altHref={primaryLinkUrl}
            disabled={!isAppMessageDataView && props.nativeDisabled}
        >
            {isAppMessageDataView ? <span>View</span> : <span>Schedule App Message</span>}
        </Button>
    )
}
